"use client";

import React, { useEffect, useRef, useState } from "react";
// Removed unused imports
import { Message } from "@/components/chat/message";
import { TypingIndicator } from "@/components/chat/typing-indicator";
import { MessageInput } from "@/components/chat/message-input";
import { WelcomeScreen } from "@/components/chat/welcome-screen";
// Removed unused RegistrationForm import
import { AstrologyDisclaimerModal } from "@/components/modals/astrology-disclaimer-modal";
import { useChatStore } from "@/store/chat-store";
import { useAuthStore } from "@/store/auth-store";

export function ChatInterface() {
  const { chats, currentChatId, addMessage, isTyping, setIsTyping } = useChatStore();
  const { isAuthenticated } = useAuthStore();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const [disclaimerChecked, setDisclaimerChecked] = useState(false);

  // Safe storage access functions
  const safeStorageGet = (key: string): string | null => {
    try {
      if (typeof window !== 'undefined') {
        // Try localStorage first
        if (window.localStorage) {
          return localStorage.getItem(key);
        }
        // Fallback to sessionStorage for incognito mode
        if (window.sessionStorage) {
          return sessionStorage.getItem(key);
        }
      }
    } catch (error) {
      console.warn('Storage access failed:', error);
    }
    return null;
  };

  const safeStorageSet = (key: string, value: string): void => {
    try {
      if (typeof window !== 'undefined') {
        // Try localStorage first
        if (window.localStorage) {
          localStorage.setItem(key, value);
          return;
        }
        // Fallback to sessionStorage for incognito mode
        if (window.sessionStorage) {
          sessionStorage.setItem(key, value);
          return;
        }
      }
    } catch (error) {
      console.warn('Storage write failed:', error);
    }
  };

  // Check if user has seen the disclaimer
  useEffect(() => {
    if (disclaimerChecked) return; // Prevent multiple checks

    // Add a small delay to ensure the page has fully loaded
    const timer = setTimeout(() => {
      const hasSeenDisclaimer = safeStorageGet('astrology-disclaimer-accepted');

      setDisclaimerChecked(true);

      // Show modal if:
      // 1. User hasn't seen it before (normal case)
      // 2. Storage is not available (incognito/private mode fallback)
      if (!hasSeenDisclaimer) {
        setShowDisclaimerModal(true);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [disclaimerChecked]);

  const handleAcceptDisclaimer = () => {
    safeStorageSet('astrology-disclaimer-accepted', 'true');
    setShowDisclaimerModal(false);
  };


  const currentChat = chats.find((chat) => chat.id === currentChatId);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [currentChat?.messages, isTyping]);

  // Don't auto-create chats - let user create manually

  const simulateAIResponse = async (userMessage: string) => {
    if (!currentChatId) return;

    setIsTyping(true);

    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 1000 + Math.random() * 2000));

    // Basit AI yanıt simülasyonu
    const responses = [
      "Sorunuzu anlıyorum. Bu konuda size yardımcı olayım.",
      "Bu ilginç bir konu. İşte benim düşüncelerim...",
      "Bu konuyu paylaştığınız için teşekkürler. Bu konuda bazı görüşler sunabilirim.",
      "Ne aradığınızı anlıyorum. Bu konuyu sizin için açıklayayım.",
      "Harika bir soru! Bu konudaki görüşlerimi paylaşayım.",
    ];

    const randomResponse = responses[Math.floor(Math.random() * responses.length)];
    const enhancedResponse = `${randomResponse}\n\n"${userMessage.slice(0, 50)}${userMessage.length > 50 ? "..." : ""}" konusunda daha derinlemesine inceleme yapabiliriz. Bu, sohbet arayüzü işlevselliğini gösteren simüle edilmiş bir yanıttır.`;

    addMessage(currentChatId, enhancedResponse, "assistant");
    setIsTyping(false);
  };

  const handleSendMessage = async (message: string) => {
    if (!currentChatId) return;

    // Add user message
    addMessage(currentChatId, message, "user");

    // Simulate AI response
    if (isAuthenticated) {
      await simulateAIResponse(message);
    } else {
      // Demo modu yanıtı
      setTimeout(() => {
        addMessage(currentChatId, "AI ile konuşmaya devam etmek için lütfen giriş yapın.", "assistant");
      }, 500);
    }
  };

  // Eğer hiç chat yoksa veya currentChat seçili değilse welcome screen göster
  if (!currentChat) {
    return <WelcomeScreen />;
  }

  return (
    <>
      <AstrologyDisclaimerModal
        isOpen={showDisclaimerModal}
        onClose={() => setShowDisclaimerModal(false)}
        onAccept={handleAcceptDisclaimer}
      />

      <div className="flex flex-col h-full">
        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto scrollbar-thin px-2 md:px-0">
          {currentChat.messages.length === 0 ? (
            <WelcomeScreen />
          ) : (
            <div>
              {currentChat.messages.map((message) => (
                <Message key={message.id} message={message} />
              ))}
              {isTyping && <TypingIndicator />}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Message Input */}
        <MessageInput
          onSendMessage={handleSendMessage}
          disabled={false}
        />
      </div>
    </>
  );
}